import { useQuery } from "@apollo/client";
import { AllInclusive, Archive, Edit } from "@mui/icons-material";
import { Chip, Typography } from "@mui/material";
import { upperFirst } from "lodash";
import { DateTime } from "luxon";
import { useConfirm } from "material-ui-confirm";
import { PropsWithChildren, ReactNode, useState } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";

import {
  ListAllBannersQuery,
  UpdateBannerMutation,
  UpdateBannerMutationVariables,
} from "~frontend/__generated__/graphql";
import Copy from "~frontend/components/icons/Copy";
import { LIST_ALL_BANNERS, UPDATE_BANNER } from "~frontend/graphql/banners";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

import { AsyncWrapper } from "../AsyncWrapper";
import PageContainer from "../PageContainer";
import Table, { RowActions, TableBox, TableControls } from "../Table";
import BannerDialog from "./BannerDialog";

type BannerType = ListAllBannersQuery["listAllBanners"][0];

function isExpired(banner: BannerType) {
  const now = DateTime.now().toMillis();
  return banner.expiresAt != null && banner.expiresAt < now;
}

// This component wraps cell content to apply styling if the banner is expired
function CellDisplay(props: PropsWithChildren<{ banner: BannerType }>) {
  const sx = isExpired(props.banner)
    ? { fontStyle: "italic", color: "text.secondary" }
    : {};
  return (
    <Typography variant="body2" sx={sx}>
      {props.children}
    </Typography>
  );
}

export function Banners(): ReactNode {
  const isStaff = useCurrentUserIsStaff();
  const confirm = useConfirm();

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const [activeFilter, setActiveFilter] = useState<"active" | "all">("all");
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [bannerDialogMode, setBannerDialogMode] = useState<"create" | "edit">(
    "create"
  );
  const [editingBanner, setEditingBanner] = useState<BannerType | null>(null);

  const {
    data: banners,
    error,
    loading,
    refetch,
  } = useQuery<ListAllBannersQuery>(LIST_ALL_BANNERS, {
    variables: {
      input: {
        onlyActive: activeFilter === "active" ? true : undefined,
      },
    },
  });

  const [updateBanner] = useMutation<
    UpdateBannerMutation,
    UpdateBannerMutationVariables
  >(UPDATE_BANNER, {
    onErrorSnackbarMessage: "Error deactivating banner",
  });

  async function handleDeactivate(banner: BannerType) {
    const { confirmed } = await confirm({
      title: "Deactivate Banner",
      description:
        "Are you sure you want to deactivate this banner? This will set the expiration date to now, immediately hiding the banner from users.",
    });
    if (!confirmed) {
      return;
    }

    await updateBanner({
      variables: {
        input: {
          id: banner.id,
          expiresAt: DateTime.now().toISO(),
        },
      },
    });

    void refetch();
  }

  if (!isStaff) {
    return null;
  }

  return (
    <PageContainer maxWidth="lg">
      <TableControls
        filters={[
          {
            id: "active",
            label: "Status",
            value: activeFilter,
            onChange: (value: string) => {
              setActiveFilter(value as "active" | "all");
              void refetch();
            },
            options: [
              { value: "active", label: "Active" },
              { value: "all", label: "All" },
            ],
          },
        ]}
        addButtons={[
          {
            label: "New Banner",
            onClick: () => {
              setEditingBanner(null);
              setBannerDialogMode("create");
              setBannerDialogOpen(true);
            },
            id: "new-banner",
          },
        ]}
      />
      <TableBox sx={{ pb: 1 }}>
        <AsyncWrapper
          data={banners?.listAllBanners}
          errors={error}
          loading={loading}
        >
          {banners => {
            // Slice banners for client-side pagination
            const paginatedBanners = banners.slice(
              page * pageSize,
              page * pageSize + pageSize
            );

            return (
              <Table<BannerType>
                columns={[
                  {
                    key: "id",
                    title: "Audience",
                    renderValue: value => {
                      let content: string;
                      if (
                        !value.tenant &&
                        !value.subaccount &&
                        !value.userEmail
                      ) {
                        content = "Global";
                      } else if (value.userEmail) {
                        content = `User: ${value.userEmail}`;
                      } else if (value.tenant && !value.subaccount) {
                        content = `Tenant: ${value.tenant.name} (${value.tenant.slug})`;
                      } else if (value.tenant && value.subaccount) {
                        content = `Tenant: ${value.tenant.name} (${value.tenant.slug}), Subaccount: ${value.subaccount.name} (${value.subaccount.slug})`;
                      } else {
                        content = "";
                      }
                      return (
                        <CellDisplay banner={value}>{content}</CellDisplay>
                      );
                    },
                  },
                  {
                    key: "level",
                    title: "Level",
                    sx: { maxWidth: 100 },
                    renderValue: value => (
                      <Chip
                        color={
                          {
                            INFO: "primary",
                            WARNING: "warning",
                            ERROR: "error",
                          }[value.level] as "primary" | "warning" | "error"
                        }
                        label={upperFirst(value.level.toLocaleLowerCase())}
                      />
                    ),
                  },
                  {
                    key: "message",
                    title: "Message",
                    sx: { minWidth: 250 },
                    align: "left",
                    renderValue: value => (
                      <CellDisplay banner={value}>{value.message}</CellDisplay>
                    ),
                  },
                  {
                    key: "startsAt",
                    title: "Active Dates",
                    sx: { minWidth: 225 },
                    renderValue: value => {
                      const startsAtDate = DateTime.fromMillis(value.startsAt);
                      const expiresAtDate = value.expiresAt
                        ? DateTime.fromMillis(value.expiresAt)
                        : null;

                      // Using toFormat here for the specific format including timezone and not seconds
                      // Might not be good for all locales, but for a staff-only view it's acceptable
                      const startsAtString = startsAtDate.toFormat(
                        "MM/dd/yyyy, hh:mm a ZZZZ"
                      );
                      const expiresAtString = expiresAtDate
                        ? "End: " +
                          expiresAtDate.toFormat("MM/dd/yyyy, hh:mm a ZZZZ")
                        : null;

                      return (
                        <CellDisplay banner={value}>
                          <>
                            Start: {startsAtString}
                            <br />
                            {expiresAtString ? (
                              expiresAtString
                            ) : (
                              <AllInclusive />
                            )}
                          </>
                        </CellDisplay>
                      );
                    },
                  },
                  {
                    key: "createdAt",
                    title: "Created At",
                    renderValue: value => {
                      return (
                        <CellDisplay banner={value}>
                          {DateTime.fromMillis(
                            value.createdAt
                          ).toLocaleString()}
                        </CellDisplay>
                      );
                    },
                  },
                  {
                    key: "createdByEmail",
                    sx: {
                      maxWidth: 100,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    title: "Created By",
                    renderValue: value => (
                      <CellDisplay banner={value}>
                        {value.createdByEmail}
                      </CellDisplay>
                    ),
                  },
                ]}
                rowActions={value => {
                  const now = DateTime.now().toMillis();
                  const isActive = !value.expiresAt || value.expiresAt > now;

                  const actions: RowActions<BannerType> = [
                    {
                      id: "edit",
                      icon: <Edit />,
                      tooltipLabel: "Edit",
                      onClick: () => {
                        setEditingBanner(value);
                        setBannerDialogMode("edit");
                        setBannerDialogOpen(true);
                      },
                    },
                    {
                      id: "duplicate",
                      icon: <Copy />,
                      tooltipLabel: "Duplicate",
                      onClick: () => {
                        setEditingBanner({
                          ...value,
                          id: "",
                          tenantId: null,
                          tenant: null,
                          subaccountId: null,
                          subaccount: null,
                          expiresAt: null,
                          startsAt: null,
                        });
                        setBannerDialogMode("create");
                        setBannerDialogOpen(true);
                      },
                    },
                  ];

                  if (isActive) {
                    actions.unshift({
                      id: "deactivate",
                      icon: <Archive />,
                      tooltipLabel: "Deactivate",
                      onClick: () => {
                        void handleDeactivate(value);
                      },
                    });
                  }

                  return actions;
                }}
                rows={paginatedBanners}
                rowsPerPage={pageSize}
                onChangeRowsPerPage={newRowsPerPage => {
                  setPageSize(newRowsPerPage);
                  setPage(0);
                }}
                page={page}
                onChangePage={setPage}
                totalCount={banners.length}
                emptyState="No banners found."
              />
            );
          }}
        </AsyncWrapper>
      </TableBox>
      <BannerDialog
        open={bannerDialogOpen}
        onClose={() => {
          setBannerDialogOpen(false);
          setEditingBanner(null);
        }}
        banner={editingBanner}
        mode={bannerDialogMode}
        refetch={refetch}
      />
    </PageContainer>
  );
}
